import { Component, Input, Output, EventEmitter, Inject, OnChanges, OnInit } from 'ng-metadata/core';
import './app-header.component.scss';
import { AppContainerStateService } from '../app-container-state.service';
import { AppContainerStateChangeName } from '../shared/shared.interfaces';

/**
 * @ngdoc directive
 * @name mwAppHeader
 * @module mw.components.appContainer
 *
 * @param {boolean} [hide-nav=false] - Determines whether the nav toggle icon button (hamburger icon) should be hidden.
 * @param {boolean} [hide-help-button=false] - Determines whether the help icon button should be hidden.
 * @param {string} [header-text] - The header title text.
 * @param {string} [side-nav-component-id='mwNavigationSideNav'] The component ID of a sidenav component.
 * @param {expression} [on-nav-toggle] Angular expression to evaluate on click of the nav icon button.  $event is the ng-click event that triggered the toggle.
 *
 * @description
 * `<mw-app-header>` is a component used to display application header toolbar.
 *
 * @usage
 * <hljs hljs-language="html">
 *   <mw-app-header>
 *      <mw-account-menu-slot></mw-user-menu-slot>
 *      <mw-content-slot></mw-content-slot>
 *      <mw-secondary-navigation-slot></mw-secondary-navigation-slot>
 *      <mw-icon-slot>
 *          <md-icon class="mw-navigation__eyecon-glyph md-primary" md-svg-src="icons/meltwater-icons/eyecon-glyph.svg"></md-icon>
 *      </mw-icon-slot>
 *      <mw-user-menu-slot></mw-user-menu-slot>
 *      <mw-notifications-slot></mw-notifications-slot>
 *   </mw-app-header>
 * </hljs>
 *
 */
@Component({
    selector: 'mw-app-header',
    template: `
        <md-toolbar class="md-whiteframe-z1 ng-animate-disabled mw-button__container--dark">
            <div class="mw-app-header md-toolbar-tools">
                <div ng-transclude="iconSlot"></div>

                <md-button ng-if="!$ctrl.hideNav" class="mw-app-header__hamburger md-icon-button md-no-ink" aria-label="Menu" ng-click="$ctrl.handleNavToggle($event)" hide-gt-sm>
                    <md-icon md-svg-icon="navigation:ic_menu_24px"></md-icon>
                </md-button>

                <div ng-transclude="contentSlot"></div>

                <span ng-if="$ctrl.headerText" class="mw-app-header__title" translate="{{$ctrl.headerText}}"></span>

                <span ng-if="$ctrl.hasSecondaryNavSlot()" class="mw-app-header__separator" hide-xs>|</span>
                <div ng-transclude="secondaryNavSlot" hide-xs></div>

                <div flex="auto"></div>

                <div class="mw-app-header__account-menu" ng-transclude="accountMenuSlot"></div>

                <md-button ng-if="$ctrl.shouldUseHelpButton === false && !$ctrl.hideHelpButton" aria-label="{{'Help' | translate}" ng-click="$ctrl.help($event)" md-no-ink class="mw-app-header__help md-icon-button mw-icon-button" hide-xs>
                    <md-tooltip md-visible="false" md-direction="bottom" md-delay="2000">
                        <span translate="Help" translate-default="Help"></span>
                    </md-tooltip>
                    <md-icon class="mw-app-header__help-icon" md-svg-icon="meltwater:helpcenter"></md-icon>
                </md-button>

                <md-button id="mw-app-header__help-button" ng-if="$ctrl.shouldUseHelpButton === true && !$ctrl.hideHelpButton" aria-label="{{'Help' | translate}" ng-click="$ctrl.help($event)" class="mw-button mw-app-header__help-button" hide-xs>
                    <span translate>{{$ctrl.helpButtonText}}</span>
                </md-button>

                <div class="mw-app-header__notifications" ng-transclude="notificationsSlot"></div>

                <div class="mw-app-header__user-menu" ng-transclude="userMenuSlot"></div>
            </div>
        </md-toolbar>
    `,
    legacy: {
        transclude: {
            'iconSlot': '?mwIconSlot',
            'contentSlot': '?mwContentSlot',
            'secondaryNavSlot': '?mwSecondaryNavigationSlot',
            'accountMenuSlot': '?mwAccountMenuSlot',
            'userMenuSlot': '?mwUserMenuSlot',
            'notificationsSlot': '?mwNotificationsSlot',
        }
    }
})
export class AppHeaderComponent implements OnChanges, OnInit {
    @Input('<') hideNav: boolean;
    @Input('<') hideHelpButton: boolean;
    @Input('@') headerText: string;
    @Input('@') sideNavComponentId: string = 'mwNavigationSideNav';

    @Output() onHelp = new EventEmitter<ng.IAngularEvent>();
    @Output() onNavToggle = new EventEmitter<ng.IAngularEvent>();

    private shouldUseHelpButton: boolean = false;
    private helpButtonText: string = 'Help';

    constructor(@Inject('$mdSidenav') private $mdSidenav: ng.material.ISidenavService,
                @Inject('mwAppContainerStateService') private mwAppContainerStateService: AppContainerStateService,
                @Inject('$rootScope') private $rootScope: ng.IRootScopeService,
                @Inject('$translate') private $translate: ng.translate.ITranslateService,
                @Inject('$window') private $window: ng.IWindowService) {
        'ngInject';
    }

    ngOnInit() {
        if (this.$window.mwUseHelpButton) {
            this.shouldUseHelpButton = this.$window.mwUseHelpButton;
        }

        this.$rootScope.$on('$translateChangeSuccess', () => {
            if (this.shouldUseHelpButton) {
                this.updateHelpTextState();
            }
            if (this.headerText && this.headerText.length) {
                this.updatePageNameState();
            }
        });
    }

    ngOnChanges(changes) {
        if (changes.headerText && changes.headerText.currentValue && (changes.headerText.currentValue != changes.headerText.previousValue)) {
            this.updatePageNameState();
        }
    }

    updatePageNameState() {
        const translatedHeaderText = this.$translate.instant(this.headerText);

        this.mwAppContainerStateService.updateAppContainerState(AppContainerStateChangeName.pageName, translatedHeaderText);
    }

    updateHelpTextState() {
        const translatedHelpButtonText = this.$translate.instant(this.helpButtonText);

        this.mwAppContainerStateService.updateAppContainerState(AppContainerStateChangeName.helpText, translatedHelpButtonText);
    }

    handleNavToggle(event: ng.IAngularEvent) {
        if (this.sideNavComponentId) {
            this.$mdSidenav(this.sideNavComponentId).toggle();
        }
        this.onNavToggle.emit(event);
    }

    help(event: ng.IAngularEvent) {
        this.onHelp.emit(event);
    }
}



// WEBPACK FOOTER //
// ./src/lib/components/app-container/app-header/app-header.component.ts